export interface DatePeriod {
  startDate: string;
  endDate: string;
}

export interface Periods {
  current: DatePeriod;
  previous: DatePeriod;
}

/**
 * Default landing window for all client dashboard tabs: the trailing 30 days
 * ending yesterday, compared to the prior 30 days. Replaces the former
 * "lastmonth" default so the dashboard always reflects the latest status
 * rather than a finalized prior calendar month. Single source of truth shared
 * by every dashboard page and the DateRangeSelector URL fallback, so rendered
 * data and the dropdown stay in sync without a redirect.
 */
export const DEFAULT_DASHBOARD_RANGE = "30d";
export const DEFAULT_DASHBOARD_COMPARE = "period";

/**
 * Shift a date backward/forward by N months, clamping the day-of-month
 * to the last valid day of the target month (handles 31→30, Feb leap, etc.).
 */
function shiftMonths(d: Date, months: number): Date {
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return target;
}

/**
 * Returns the calendar-correct previous period for ranges that name a
 * calendar unit. Returns null for ranges where equal-day-span is correct
 * (lastweek, Nd, Nm, today, yesterday, custom:, alltime).
 */
function computeCalendarPrevious(
  range: string,
  currentStart: Date,
  currentEnd: Date
): { previousStart: Date; previousEnd: Date } | null {
  if (range === "lastmonth") {
    const previousStart = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth() - 1,
      1
    );
    const previousEnd = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth(),
      0
    );
    return { previousStart, previousEnd };
  }

  if (range === "lastquarter") {
    const previousStart = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth() - 3,
      1
    );
    const previousEnd = new Date(
      currentStart.getFullYear(),
      currentStart.getMonth(),
      0
    );
    return { previousStart, previousEnd };
  }

  if (range === "lastyear") {
    const year = currentStart.getFullYear() - 1;
    return {
      previousStart: new Date(year, 0, 1),
      previousEnd: new Date(year, 11, 31),
    };
  }

  if (range === "thismonth") {
    return {
      previousStart: shiftMonths(currentStart, -1),
      previousEnd: shiftMonths(currentEnd, -1),
    };
  }

  if (range === "thisquarter") {
    return {
      previousStart: shiftMonths(currentStart, -3),
      previousEnd: shiftMonths(currentEnd, -3),
    };
  }

  if (range === "thisyear") {
    return {
      previousStart: shiftMonths(currentStart, -12),
      previousEnd: shiftMonths(currentEnd, -12),
    };
  }

  return null;
}

export function computePeriods(range: string, compareMode = "period", referenceDate?: Date): Periods {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const today = referenceDate ? new Date(referenceDate) : new Date();
  today.setHours(0, 0, 0, 0);

  let currentStart: Date;
  let currentEnd: Date;

  if (range.startsWith("custom:")) {
    const [, startStr, endStr] = range.split(":");
    currentStart = new Date(startStr + "T00:00:00");
    currentEnd = new Date(endStr + "T00:00:00");
  } else if (range === "today") {
    currentStart = new Date(today);
    currentEnd = new Date(today);
  } else if (range === "yesterday") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    currentStart = new Date(y);
    currentEnd = new Date(y);
  } else if (range === "alltime") {
    currentStart = new Date("2000-01-01T00:00:00");
    currentEnd = new Date(today);
  } else if (range === "lastweek") {
    // Mon–Sun of the previous calendar week
    const dow = today.getDay(); // 0=Sun, 1=Mon…
    const daysToThisMon = dow === 0 ? 6 : dow - 1;
    const thisMon = new Date(today);
    thisMon.setDate(today.getDate() - daysToThisMon);
    currentStart = new Date(thisMon);
    currentStart.setDate(thisMon.getDate() - 7);
    currentEnd = new Date(thisMon);
    currentEnd.setDate(thisMon.getDate() - 1);
  } else if (range === "lastmonth") {
    const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
    currentEnd = new Date(firstOfThis);
    currentEnd.setDate(0); // last day of previous month
    currentStart = new Date(currentEnd.getFullYear(), currentEnd.getMonth(), 1);
  } else if (range === "thismonth") {
    currentStart = new Date(today.getFullYear(), today.getMonth(), 1);
    currentEnd = new Date(today);
  } else if (range === "thisquarter") {
    const qStartMonth = Math.floor(today.getMonth() / 3) * 3;
    currentStart = new Date(today.getFullYear(), qStartMonth, 1);
    currentEnd = new Date(today);
  } else if (range === "lastquarter") {
    const curQStartMonth = Math.floor(today.getMonth() / 3) * 3;
    const prevQStartMonth = curQStartMonth - 3;
    const year = prevQStartMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = prevQStartMonth < 0 ? prevQStartMonth + 12 : prevQStartMonth;
    currentStart = new Date(year, month, 1);
    currentEnd = new Date(today.getFullYear(), curQStartMonth, 0); // last day before current quarter
  } else if (range === "thisyear") {
    currentStart = new Date(today.getFullYear(), 0, 1);
    currentEnd = new Date(today);
  } else if (range === "lastyear") {
    const year = today.getFullYear() - 1;
    currentStart = new Date(year, 0, 1);
    currentEnd = new Date(year, 11, 31);
  } else if (/^\d+m$/.test(range)) {
    // Last X calendar months, ending yesterday
    const months = parseInt(range, 10);
    currentEnd = new Date(today);
    currentEnd.setDate(today.getDate() - 1);
    // shiftMonths clamps day-of-month so a month-end reference date can't
    // overflow into the following month (raw setMonth on May 31 - 3 months
    // lands on Mar 3, silently shrinking the window).
    currentStart = shiftMonths(today, -months);
  } else {
    // Xd — last X days ending yesterday (handles 7d, 30d, 90d, 365d, any Nd)
    const days = parseInt(range, 10) || 30;
    currentEnd = new Date(today);
    currentEnd.setDate(today.getDate() - 1);
    currentStart = new Date(currentEnd);
    currentStart.setDate(currentEnd.getDate() - days + 1);
  }

  let previousStart: Date;
  let previousEnd: Date;

  if (compareMode === "year") {
    previousStart = new Date(currentStart);
    previousStart.setFullYear(currentStart.getFullYear() - 1);
    previousEnd = new Date(currentEnd);
    previousEnd.setFullYear(currentEnd.getFullYear() - 1);
  } else if (compareMode.startsWith("custom:")) {
    const parts = compareMode.split(":");
    previousStart = new Date(parts[1] + "T00:00:00");
    previousEnd = new Date(parts[2] + "T00:00:00");
  } else {
    // "period": prior calendar unit for named ranges, otherwise equal span
    const calendarPrev = computeCalendarPrevious(range, currentStart, currentEnd);
    if (calendarPrev) {
      previousStart = calendarPrev.previousStart;
      previousEnd = calendarPrev.previousEnd;
    } else {
      // For relative ranges without calendar semantics (e.g., 7d, 30d), the span
      // is the day count; since dates are inclusive on both ends, we add 1.
      const spanMs = currentEnd.getTime() - currentStart.getTime();
      const spanDays = Math.max(1, Math.round(spanMs / 86_400_000) + 1);
      previousEnd = new Date(currentStart);
      previousEnd.setDate(currentStart.getDate() - 1);
      previousStart = new Date(previousEnd);
      previousStart.setDate(previousEnd.getDate() - spanDays + 1);
    }
  }

  return {
    current: { startDate: fmt(currentStart), endDate: fmt(currentEnd) },
    previous: { startDate: fmt(previousStart), endDate: fmt(previousEnd) },
  };
}

/**
 * Returns true if the given date range is fully in the past (closed/finalized).
 * Closed ranges will never produce new data, so cached data is permanently fresh.
 */
const RANGE_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  lastweek: "Last Week",
  thismonth: "This Month",
  lastmonth: "Last Month",
  thisquarter: "This Quarter",
  lastquarter: "Last Quarter",
  thisyear: "This Year",
  lastyear: "Last Year",
  alltime: "All Time",
};

function formatLabelDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Human label for a stored range key, matching the date-range selector's own
 * option text ("30d" reads "Last 30 Days", "lastmonth" reads "Last Month").
 * Custom ranges spell out both dates. Unknown keys fall back to the key so a
 * surprise value is visible rather than blank.
 */
export function formatRangeLabel(range: string): string {
  if (RANGE_LABELS[range]) return RANGE_LABELS[range];
  const days = /^(\d+)d$/.exec(range);
  if (days) return `Last ${Number(days[1])} Days`;
  const months = /^(\d+)m$/.exec(range);
  if (months) return `Last ${Number(months[1])} Months`;
  if (range.startsWith("custom:")) {
    const [, start, end] = range.split(":");
    if (start && end) return `${formatLabelDate(start)} to ${formatLabelDate(end)}`;
  }
  return range;
}

export function isClosedRange(range: string): boolean {
  // Ranges that always include today or ongoing data
  if (["today", "thismonth", "thisweek", "thisquarter", "thisyear", "alltime"].includes(range)) {
    return false;
  }
  // Completed past periods
  if (["yesterday", "lastweek", "lastmonth", "lastquarter", "lastyear"].includes(range)) {
    return true;
  }
  // Custom range — check if end date is before today
  if (range.startsWith("custom:")) {
    const [, , endStr] = range.split(":");
    const end = new Date(endStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end < today;
  }
  // Xd and Xm ranges end yesterday — closed
  if (/^\d+[dm]$/.test(range)) {
    return true;
  }
  return false;
}

/**
 * True when a range resolves to a fixed window that never moves over calendar
 * time. Only custom ranges carry explicit start:end dates; every other "closed"
 * range (lastmonth, 7d, ...) is relative and its window slides at the day
 * boundary, so it must be revalidated rather than cached forever.
 */
export function isAbsoluteRange(range: string): boolean {
  return range.startsWith("custom:");
}

/** True when two dates fall on the same calendar day in server-local time. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Decides whether a cached row for a CLOSED date range may be served as-is
 * without a refetch. This is the gate that guards against the weekly-brief
 * "frozen window" bug: a relative closed range (lastweek, lastmonth, 7d, ...)
 * shares a static cache key but resolves to a window that slides at the day
 * boundary, so a row written on a previous day represents a stale window and
 * MUST be revalidated. Only absolute custom ranges carry explicit start:end
 * dates and are therefore immutable — they may be served forever.
 *
 * Returns false for open/ongoing ranges (thismonth, today, ...); the caller
 * still applies the normal TTL check after this returns false.
 */
export function canServeClosedCache(
  dateRange: string,
  fetchedAt: Date,
  now: Date
): boolean {
  if (!isClosedRange(dateRange)) return false;
  return isAbsoluteRange(dateRange) || isSameDay(fetchedAt, now);
}

// "Jun 3" style short day for an ISO date (local-time midnight, no TZ shift)
export function formatShortDay(iso: string): string {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatDelta(
  current: number,
  previous: number
): { value: number; pct: number; direction: "up" | "down" | "flat" } {
  const value = current - previous;
  const pct =
    previous !== 0 ? Math.round((value / Math.abs(previous)) * 1000) / 10 : 0;
  const direction: "up" | "down" | "flat" =
    value > 0 ? "up" : value < 0 ? "down" : "flat";
  return { value, pct, direction };
}
